import { useEffect, useRef, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePlayerDetail } from '../../hooks/useLeagues'
import { lockScroll, unlockScroll } from '../../lib/scrollLock'
import { getTeamColor } from '../../lib/teamColors'
import LoadingSpinner from '../ui/LoadingSpinner'
import { nflTeamNickname } from '../../lib/nflTeamNames'

const INJURY_COLORS = {
  Out: 'text-incorrect',
  IR: 'text-incorrect',
  Questionable: 'text-yellow-400',
  Doubtful: 'text-yellow-400',
  Probable: 'text-correct',
  'Day-To-Day': 'text-yellow-400',
}

function InjuryBadge({ status }) {
  if (!status) return null
  return (
    <span className={`text-[12px] font-mono font-bold px-2 py-0.5 rounded ${INJURY_COLORS[status] || 'text-text-muted'}`}>
      {status}
    </span>
  )
}

// Raw ESPN/Sleeper codes that mean "individual defender". Sleeper files
// nearly every defensive back as DB, so this list is wider than the four
// slot names.
const IDP_POSITION_CODES = ['DL', 'LB', 'DB', 'S', 'DE', 'DT', 'NT', 'ILB', 'OLB', 'MLB', 'CB', 'FS', 'SS']

// Offensive counterpart to IDP_COLUMN_DEFS below, for the mirror case: a
// player whose base position is defensive but who touches the ball.
const OFFENSE_COLUMN_DEFS = [
  { key: 'rec_tgt', label: 'TGT' },
  { key: 'rec', label: 'REC' },
  { key: 'rec_yd', label: 'ReYD' },
  { key: 'rec_td', label: 'ReTD' },
  { key: 'rush_att', label: 'CAR' },
  { key: 'rush_yd', label: 'RuYD' },
  { key: 'rush_td', label: 'RuTD' },
]

const IDP_COLUMN_DEFS = [
  { key: 'idp_tkl_solo', label: 'SOLO' },
  { key: 'idp_tkl_ast', label: 'AST' },
  { key: 'idp_sack', label: 'SK' },
  { key: 'idp_tkl_loss', label: 'TFL' },
  { key: 'idp_int', label: 'INT' },
  { key: 'idp_pass_def', label: 'PD' },
  { key: 'idp_ff', label: 'FF' },
  { key: 'idp_fum_rec', label: 'FR' },
  // Scores 6 and had no column, so a pick-six was invisible in the grid.
  { key: 'def_td', label: 'TD' },
]

/**
 * Columns for a player's weekly table.
 *
 * Two things the raw position string could not express:
 *
 * 1. Dual-position players. The server sends Travis Hunter as "WR/DB", which
 *    matched neither the offensive branches nor the IDP list, so he fell
 *    through to receiving-only — hiding tackles his league actually pays for
 *    (13 of his 62.8 points in 2025).
 * 2. Offensive players who record a stray defensive stat. Every scoring
 *    preset carries non-zero idp_* values, so a receiver who makes the tackle
 *    after an interception genuinely earns a point, and nothing on screen
 *    explained where it came from.
 *
 * Defensive columns are therefore appended by production rather than by
 * position — the same approach the kicker branch already uses for rushing and
 * receiving. A pure defender is unaffected (his branch already lists all
 * eight). Hunter picks up three. A receiver with one tackle picks up one,
 * on the single player where it explains a point.
 */
function columnsFor(position, weeks) {
  const parts = String(position || '').split('/').map((p) => p.trim()).filter(Boolean)
  const offensePart = parts.find((p) => !IDP_POSITION_CODES.includes(p) && p !== 'DEF')
  const basePosition = offensePart || parts[0] || position

  const base = baseColumnsFor(basePosition, weeks)
  const seen = new Set(base.map((c) => c.key))
  const produced = (key) => (weeks || []).some((w) => (Number(w[key]) || 0) !== 0)
  const extras = IDP_COLUMN_DEFS.filter((c) => !seen.has(c.key) && produced(c.key))

  // The mirror of the rule above, and for the same reason.
  //
  // Appending IDP columns by production fixed the receiver who makes a
  // tackle. It did NOT fix the other direction: Travis Hunter is stored as
  // plain 'DB', so he takes the IDP branch and his receiving line — 4 for 59
  // and a touchdown in week 6, most of that week's 15.9 points — had no
  // columns to render in. The points were right; the table simply could not
  // show where they came from.
  //
  // The dual-position path above expects the server to send 'WR/DB', which
  // comes from player_position_overrides. No such row exists for him, and
  // adding one would change which roster slots he can fill mid-season — a
  // league-rules decision, not a display one. Keying off production needs no
  // override and covers any two-way player automatically.
  // Scoped to a DEFENSIVE base position on purpose. Applied everywhere it
  // would also hand a CAR column to any receiver who ever took a handoff,
  // which is a change to tables that are already correct.
  const isDefensiveBase = IDP_POSITION_CODES.includes(basePosition) || basePosition === 'DEF'
  const offenseExtras = isDefensiveBase
    ? OFFENSE_COLUMN_DEFS.filter((c) => !seen.has(c.key) && produced(c.key))
    : []

  // Offence first: it is what the player did with the ball, and it keeps the
  // column order consistent with every other skill position's table.
  const cols = [...base, ...offenseExtras, ...extras]

  // Snap share goes LAST, after the IDP extras, so it is the far-right
  // column in every position's table — context you scroll to, not something
  // competing with the yards and touchdowns.
  //
  // Only shown when at least one week actually reports it. Weeks before snap
  // capture began have none, and a column of dashes for a 2025 player would
  // read as broken rather than as absent data.
  const hasSnaps = (weeks || []).some((w) => w.snap_pct != null)
  return hasSnaps ? [...cols, { key: 'snap_pct', label: 'SNP%' }] : cols
}

/**
 * Returns the column set used in the per-week stats table for a given position.
 * Skill positions share the same set; QB / K / DEF have their own. For K,
 * trick-play stats (rush/rec/pass) are appended only when at least one
 * tracked week has a non-zero value — keeps the normal kicker table tight
 * while exposing the source of unexpected points when they appear.
 */
function baseColumnsFor(position, weeks) {
  if (position === 'QB') {
    return [
      { key: 'pts', label: 'Pts' },
      { key: 'pass_cmp', label: 'CMP' },
      { key: 'pass_att', label: 'ATT' },
      { key: 'pass_yd', label: 'PaYD' },
      { key: 'pass_td', label: 'PaTD' },
      { key: 'pass_int', label: 'INT' },
      { key: 'rush_yd', label: 'RuYD' },
      { key: 'rush_td', label: 'RuTD' },
      // Trick plays — QB rarely gets these but we show them when they happen
      { key: 'rec_tgt', label: 'TGT' },
      { key: 'rec', label: 'REC' },
      { key: 'rec_yd', label: 'ReYD' },
      { key: 'rec_td', label: 'ReTD' },
    ]
  }
  if (position === 'K') {
    const cols = [
      { key: 'pts', label: 'Pts' },
      { key: 'fgm', label: 'FG' },
      { key: 'fgm_50_plus', label: '50+' },
      { key: 'fgmiss', label: 'Miss' },
      { key: 'xpm', label: 'XP' },
    ]
    const anyNonZero = (key) => (weeks || []).some((w) => (Number(w[key]) || 0) !== 0)
    if (anyNonZero('rush_yd') || anyNonZero('rush_td')) {
      cols.push({ key: 'rush_yd', label: 'RuYD' }, { key: 'rush_td', label: 'RuTD' })
    }
    if (anyNonZero('rec') || anyNonZero('rec_yd') || anyNonZero('rec_td')) {
      cols.push({ key: 'rec', label: 'REC' }, { key: 'rec_yd', label: 'ReYD' }, { key: 'rec_td', label: 'ReTD' })
    }
    if (anyNonZero('pass_yd') || anyNonZero('pass_td')) {
      cols.push({ key: 'pass_yd', label: 'PaYD' }, { key: 'pass_td', label: 'PaTD' })
    }
    return cols
  }
  if (position === 'DEF') {
    return [
      { key: 'pts', label: 'Pts' },
      { key: 'def_sack', label: 'SK' },
      { key: 'def_int', label: 'INT' },
      { key: 'def_fum_rec', label: 'FR' },
      { key: 'def_td', label: 'TD' },
      { key: 'def_safety', label: 'SAF' },
      { key: 'def_pts_allowed', label: 'PA' },
    ]
  }
  // Individual defensive players — DL/LB/DB/S. Raw ESPN codes (DE/DT/NT,
  // ILB/OLB/MLB, CB, FS/SS) all fall into this branch too.
  if (IDP_POSITION_CODES.includes(position)) {
    return [
      { key: 'pts', label: 'Pts' },
      { key: 'idp_tkl_solo', label: 'SOLO' },
      { key: 'idp_tkl_ast', label: 'AST' },
      { key: 'idp_sack', label: 'SK' },
      { key: 'idp_tkl_loss', label: 'TFL' },
      { key: 'idp_int', label: 'INT' },
      { key: 'idp_pass_def', label: 'PD' },
      { key: 'idp_ff', label: 'FF' },
      { key: 'idp_fum_rec', label: 'FR' },
      { key: 'def_td', label: 'TD' },
    ]
  }
  if (position === 'RB') {
    // Rushing first for RBs, then receiving since modern RBs catch a lot too
    return [
      { key: 'pts', label: 'Pts' },
      { key: 'rush_att', label: 'ATT' },
      { key: 'rush_yd', label: 'RuYD' },
      { key: 'rush_td', label: 'RuTD' },
      { key: 'rec_tgt', label: 'TGT' },
      { key: 'rec', label: 'REC' },
      { key: 'rec_yd', label: 'ReYD' },
      { key: 'rec_td', label: 'ReTD' },
      { key: 'fum_lost', label: 'FUM' },
    ]
  }
  // WR / TE — receiving first, rushing trailing
  return [
    { key: 'pts', label: 'Pts' },
    { key: 'rec_tgt', label: 'TGT' },
    { key: 'rec', label: 'REC' },
    { key: 'rec_yd', label: 'ReYD' },
    { key: 'rec_td', label: 'ReTD' },
    { key: 'rush_yd', label: 'RuYD' },
    { key: 'rush_td', label: 'RuTD' },
    { key: 'fum_lost', label: 'FUM' },
  ]
}

// Defensive sentences for one week. Returns [] when there was no defensive
// production, so callers can append unconditionally.
function idpNarrativeParts(week) {
  const parts = []
  const tkl = (week.idp_tkl_solo || 0) + (week.idp_tkl_ast || 0)
  if (tkl) parts.push(`${tkl} tackle${tkl !== 1 ? 's' : ''} (${week.idp_tkl_solo || 0} solo${week.idp_tkl_ast ? `, ${week.idp_tkl_ast} ast` : ''})`)
  if (week.idp_sack) parts.push(`${week.idp_sack} sack${week.idp_sack !== 1 ? 's' : ''}`)
  if (week.idp_tkl_loss) parts.push(`${week.idp_tkl_loss} TFL`)
  if (week.idp_int) parts.push(`${week.idp_int} interception${week.idp_int !== 1 ? 's' : ''}`)
  if (week.idp_pass_def) parts.push(`${week.idp_pass_def} pass${week.idp_pass_def !== 1 ? 'es' : ''} defended`)
  if (week.idp_ff) parts.push(`${week.idp_ff} forced fumble${week.idp_ff !== 1 ? 's' : ''}`)
  if (week.idp_fum_rec) parts.push(`${week.idp_fum_rec} fumble recovery${week.idp_fum_rec !== 1 ? 'ies' : ''}`)
  // A defensive touchdown is the single biggest thing a defender can do — 6
  // points, more than every tackle in a typical game combined — and it was
  // the one line item the summary never mentioned. Talanoa Hufanga's week 3
  // read "5 tackles. 1 sack. 1 TFL. 2 interceptions. 3 passes defended." for
  // a 26-point game whose pick-six supplied 6 of them.
  if (week.def_td) parts.push(`${week.def_td} defensive touchdown${week.def_td !== 1 ? 's' : ''}`)
  return parts
}

function CurrentWeekNarrative({ position, week, hideFantasyPoints = false }) {
  if (!week) {
    return <p className="text-sm text-text-muted text-center">No stats yet this week.</p>
  }
  const pts = week.pts != null ? Number(week.pts).toFixed(1) : null
  const parts = []

  if (position === 'QB') {
    if (week.pass_cmp && week.pass_att) parts.push(`Completed ${week.pass_cmp} of ${week.pass_att} pass attempts for ${week.pass_yd || 0} yards and ${week.pass_td || 0} touchdown${week.pass_td !== 1 ? 's' : ''}`)
    if (week.pass_int) parts.push(`${week.pass_int} interception${week.pass_int !== 1 ? 's' : ''}`)
    if (week.rush_yd) parts.push(`${week.rush_yd} rushing yards on ${week.rush_att || '?'} carries${week.rush_td ? `, ${week.rush_td} rushing TD` : ''}`)
  } else if (position === 'K') {
    if (week.fgm) parts.push(`${week.fgm} field goal${week.fgm !== 1 ? 's' : ''} made${week.fgm_50_plus ? ` (${week.fgm_50_plus} from 50+)` : ''}`)
    // Misses carry a penalty (-3 / -2 / -1 by range) and were shown nowhere,
    // so a kicker's total read lower than his line explained. Named by range
    // where we have it, since the penalty differs by range.
    {
      const misses = [
        week.fgmiss_0_39 ? `${week.fgmiss_0_39} under 40` : null,
        week.fgmiss_40_49 ? `${week.fgmiss_40_49} from 40-49` : null,
        week.fgmiss_50_plus ? `${week.fgmiss_50_plus} from 50+` : null,
      ].filter(Boolean)
      if (misses.length) parts.push(`${misses.join(', ')} missed`)
    }
    if (week.xpm) parts.push(`${week.xpm} extra point${week.xpm !== 1 ? 's' : ''} made`)
    // Trick-play stats — rare for kickers, but if they exist they contribute
    // to the total points, so surface them rather than leaving the user
    // wondering where the extra pts came from.
    if (week.rush_yd || week.rush_td) parts.push(`${week.rush_yd || 0} rushing yards${week.rush_td ? `, ${week.rush_td} rushing TD` : ''}`)
    if (week.rec || week.rec_yd) parts.push(`${week.rec || 0} reception${week.rec !== 1 ? 's' : ''} for ${week.rec_yd || 0} yards${week.rec_td ? `, ${week.rec_td} rec TD` : ''}`)
    if (week.pass_yd || week.pass_td) parts.push(`${week.pass_yd || 0} passing yards${week.pass_td ? `, ${week.pass_td} pass TD` : ''}`)
  } else if (position === 'DEF') {
    const items = []
    if (week.def_sack) items.push(`${week.def_sack} sack${week.def_sack !== 1 ? 's' : ''}`)
    if (week.def_int) items.push(`${week.def_int} interception${week.def_int !== 1 ? 's' : ''}`)
    if (week.def_fum_rec) items.push(`${week.def_fum_rec} fumble recovery${week.def_fum_rec !== 1 ? 'ies' : ''}`)
    if (week.def_td) items.push(`${week.def_td} defensive TD`)
    if (week.def_safety) items.push(`${week.def_safety} safety${week.def_safety !== 1 ? 's' : ''}`)
    if (items.length) parts.push(items.join(', '))
    if (week.def_pts_allowed != null) parts.push(`Allowed ${week.def_pts_allowed} points`)
  } else if (IDP_POSITION_CODES.includes(position)) {
    parts.push(...idpNarrativeParts(week))
  } else if (position === 'RB') {
    if (week.rush_att) parts.push(`${week.rush_yd || 0} rushing yards on ${week.rush_att} carries${week.rush_td ? `, ${week.rush_td} TD` : ''}`)
    if (week.rec) parts.push(`${week.rec} reception${week.rec !== 1 ? 's' : ''} for ${week.rec_yd || 0} yards${week.rec_td ? `, ${week.rec_td} TD` : ''}`)
    if (week.fum_lost) parts.push(`${week.fum_lost} fumble${week.fum_lost !== 1 ? 's' : ''} lost`)
  } else {
    // WR / TE — only render the receptions sentence when there was actual
    // receiving activity (catches, yards, or targets). Without this guard
    // a zero-activity row reads "0 receptions for 0 yards on ? targets"
    // which is louder than just falling through to "No significant stats
    // recorded."
    if (week.rec || week.rec_yd || week.rec_tgt) {
      const tgtStr = week.rec_tgt ? `${week.rec_tgt}` : '?'
      parts.push(`${week.rec || 0} reception${week.rec !== 1 ? 's' : ''} for ${week.rec_yd || 0} yards${week.rec_td ? `, ${week.rec_td} TD` : ''} on ${tgtStr} targets`)
    }
    if (week.rush_yd || week.rush_att) parts.push(`${week.rush_yd || 0} rushing yards${week.rush_att ? ` on ${week.rush_att} carries` : ''}${week.rush_td ? `, ${week.rush_td} rushing TD` : ''}`)
    if (week.fum_lost) parts.push(`${week.fum_lost} fumble${week.fum_lost !== 1 ? 's' : ''} lost`)
  }

  // The chain above picks exactly one branch by position, which leaves out
  // defense for anyone not filed as a defender — a two-way player like
  // Travis Hunter ("WR/DB"), or a receiver who made the tackle after an
  // interception. Both score for it, so both should read as having done it.
  // Guarded on production, so nothing changes for the vast majority.
  if (position !== 'DEF' && !IDP_POSITION_CODES.includes(position)) {
    parts.push(...idpNarrativeParts(week))
  }

  // Return yardage sits outside the position chain for the same reason as
  // the defensive block above: a returner is as often a WR or RB as a
  // defensive back, and it scores for all of them now.
  const retYds = (Number(week.kr_yd) || 0) + (Number(week.pr_yd) || 0)
  if (retYds) parts.push(`${retYds} return yards`)
  if (week.idp_int_ret_yd) parts.push(`${week.idp_int_ret_yd} interception return yards`)

  return (
    <div>
      {/* The narrative stat line stays in a non-fantasy contest — it's just a
          readable summary of what he did. The points figure does not: with no
          fantasy_settings row the server computes it from a default preset,
          so it would be a number this contest never used. */}
      {!hideFantasyPoints && pts && <div className="font-display text-lg text-white mb-1">{pts} pts</div>}
      <p className="text-sm text-text-primary leading-relaxed">
        {parts.length > 0 ? parts.join('. ') + '.' : 'No significant stats recorded.'}
      </p>
    </div>
  )
}

function PreviousGamesTable({ position, weeks, currentWeek, hideFantasyPoints = false }) {
  // Combined played + upcoming weeks. Server emits an `opponent` and
  // `is_home` on every row + a `played` boolean — upcoming weeks have
  // null stats and muted styling so users see the full season's
  // schedule at a glance.
  const allWeeks = weeks || []
  if (!allWeeks.length) {
    return <p className="text-xs text-text-muted text-center py-3">No games scheduled.</p>
  }
  const playedRows = allWeeks.filter((w) => w.played !== false)
  // Contests without fantasy scoring (survivor, single-stat) show the same
  // card minus the Pts column — the number would be computed from a default
  // preset that has nothing to do with how the contest actually scores.
  const columns = columnsFor(position, [...(playedRows || []), ...(currentWeek ? [currentWeek] : [])])
    .filter((c) => !(hideFantasyPoints && c.key === 'pts'))
  // Sum each column across played weeks for the season totals footer.
  // def_pts_allowed reads as a sum of all per-game points allowed, which
  // is informative enough for a season view; not worth special-casing.
  const totals = {}
  for (const c of columns) {
    let sum = 0
    for (const w of playedRows) {
      const v = Number(w[c.key]) || 0
      sum += v
    }
    totals[c.key] = sum
  }
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase text-text-muted">
            {/* Wk pins so the row stays identifiable once the table scrolls
                sideways — already needed at a QB's 14 columns, more so now
                that a two-way player can add defensive ones. */}
            <th className="text-left font-semibold pl-2 pr-1 py-2 w-6 sticky left-0 z-20 bg-bg-primary">Wk</th>
            <th className="text-left font-semibold pl-1 pr-2 py-2 whitespace-nowrap">Opp</th>
            {columns.map((c) => (
              <th key={c.key} className="text-right font-semibold px-2 py-2 whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allWeeks.map((w) => {
            const isPlayed = w.played !== false
            const oppLabel = w.opponent
              ? `${w.is_home ? 'vs' : '@'} ${w.opponent}`
              : 'BYE'
            const oppColor = !w.opponent
              ? 'text-yellow-400 font-semibold'
              : isPlayed ? 'text-text-primary' : 'text-text-muted'
            return (
              <tr key={w.week} className="border-t border-text-primary/10">
                <td className={`pl-2 pr-1 py-2 font-semibold w-6 sticky left-0 z-10 bg-bg-primary ${isPlayed ? '' : 'text-text-muted'}`}>{w.week}</td>
                <td className={`pl-1 pr-2 py-2 whitespace-nowrap ${oppColor}`}>{oppLabel}</td>
                {columns.map((c) => {
                  const val = w[c.key]
                  // For unplayed weeks: stat columns stay blank, but the
                  // pts column shows the forward-looking projection
                  // (italic + gray) so users see expected output for
                  // upcoming games at a glance.
                  const isPtsCol = c.key === 'pts'
                  const projForCell = !isPlayed && isPtsCol && w.projected_pts != null
                    ? Number(w.projected_pts).toFixed(1)
                    : null
                  const display = !isPlayed
                    ? (projForCell ?? '—')
                    : val == null
                      ? '—'
                      : isPtsCol ? Number(val).toFixed(1)
                      : c.key === 'snap_pct' ? `${val}%`
                      : val
                  return (
                    <td
                      key={c.key}
                      className={`px-2 py-2 text-right whitespace-nowrap ${
                        !isPlayed
                          ? (projForCell ? 'italic text-text-secondary' : 'text-text-muted/60')
                          : isPtsCol ? 'text-white font-semibold'
                          : 'text-text-primary'
                      }`}
                    >
                      {display}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-text-primary/20 bg-bg-card/40">
            {/* The row tint is translucent and a sticky cell needs an opaque
                base, or scrolled columns show through it. Paint the base,
                then re-apply the tint on top so the cell matches its row.
                position:sticky is itself positioned, so the overlay anchors
                here without needing `relative` (which would fight it). */}
            <td className="pl-2 pr-1 py-2 font-semibold uppercase tracking-wider text-text-muted w-6 sticky left-0 z-10 bg-bg-primary">
              <span className="absolute inset-0 bg-bg-card/40 pointer-events-none" aria-hidden="true" />
              <span className="relative">Total</span>
            </td>
            <td className="pl-1 pr-2 py-2" />{/* Opp column has no total */}
            {columns.map((c) => (
              <td
                key={c.key}
                className={`px-2 py-2 text-right whitespace-nowrap font-semibold ${c.key === 'pts' ? 'text-accent' : 'text-text-primary'}`}
              >
                {c.key === 'pts' ? totals[c.key].toFixed(1) : totals[c.key]}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function PlayerNotesSection({ blurbs, blurb, injuryDetail }) {
  const [showPrev, setShowPrev] = useState(false)

  // Prefer the full history array; fall back to the legacy single-blurb shape.
  const list = Array.isArray(blurbs) && blurbs.length ? blurbs : (blurb ? [blurb] : [])
  const current = list[0]
  const previous = list.slice(1)

  // Fallback to ESPN injury text when there's no blurb at all
  let currentText = current?.content?.trim()
  if (!currentText) {
    const parts = []
    if (injuryDetail?.body_part) parts.push(injuryDetail.body_part)
    if (injuryDetail?.detail) parts.push(injuryDetail.detail)
    currentText = parts.join(' — ')
  }
  if (!currentText) return null

  function periodLabel(b) {
    if (!b?.published_at) return ''
    return new Date(b.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="text-xs uppercase tracking-wider text-accent font-semibold">Player Notes</div>
        {current && periodLabel(current) && (
          <div className="text-[10px] text-text-muted">
            {current.generated_by === 'espn' && <span className="mr-1.5">via ESPN</span>}
            {periodLabel(current)}
          </div>
        )}
      </div>
      <p className="text-sm text-text-primary leading-relaxed">{currentText}</p>

      {previous.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowPrev((v) => !v)}
            className="text-xs text-accent hover:text-accent/80 transition-colors font-semibold"
          >
            {showPrev ? 'Hide previous notes' : `See previous notes (${previous.length})`}
          </button>
          {showPrev && (
            <div className="mt-2 space-y-3 border-l-2 border-text-primary/10 pl-3">
              {previous.map((b) => (
                <div key={b.id}>
                  <div className="text-[10px] text-text-muted mb-1">{periodLabel(b)}</div>
                  <p className="text-xs text-text-secondary leading-relaxed">{b.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function PlayerDetailModal({ leagueId, playerId, onClose, playerContext, onDrop, dropBlockedReason = null, onTrade, onClaim, onAdd, hideFantasyPoints = false }) {
  const { data, isLoading } = usePlayerDetail(leagueId, playerId)
  const contentRef = useRef(null)

  useEffect(() => {
    lockScroll()
    return () => unlockScroll()
  }, [])

  return (
    createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-16"
      onClick={onClose}
      onTouchMove={(e) => {
        // Prevent background scroll on iOS — only allow scroll inside the modal content
        if (!contentRef.current?.contains(e.target)) e.preventDefault()
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        ref={contentRef}
        className="relative bg-bg-primary border border-text-primary/20 w-full md:max-w-xl rounded-2xl max-h-[75vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose} className="absolute top-3 right-3 text-text-muted hover:text-text-primary p-1 z-10">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {isLoading || !data ? (
          <div className="p-10 flex items-center justify-center"><LoadingSpinner /></div>
        ) : (() => {
          // Fantasy is NFL-only today. Look up team color for the
          // hero backdrop; falls back to the default surface if the
          // player has no team (FA) or the color's missing.
          const teamColor = getTeamColor('americanfootball_nfl', data.player.team)
          return (
          <div>
          <div
            className="p-5"
            style={teamColor ? {
              background: `linear-gradient(180deg, ${teamColor} 0%, ${teamColor}cc 60%, ${teamColor}00 100%)`,
            } : undefined}
          >
            {/* Headshot + name top center */}
            <div className="flex flex-col items-center text-center">
              {data.player.headshot_url && (
                <img
                  src={data.player.headshot_url}
                  alt={data.player.full_name}
                  className="w-32 h-32 rounded-full object-cover bg-bg-card border-2 border-text-primary/20 mb-3"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              )}
              <h2 className="font-display text-2xl text-text-primary">{data.player.full_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-text-muted">{data.player.position} · {nflTeamNickname(data.player.team) || data.player.team || 'FA'}</span>
                <InjuryBadge status={data.player.injury_status} />
              </div>
              {!hideFantasyPoints && data.season_summary && data.season_summary.games_played > 0 && (
                <div className="mt-2 text-xs md:text-sm text-text-muted">
                  Season: <span className="text-text-primary font-semibold">{data.season_summary.total_pts} pts</span>
                  {' · '}
                  <span className="text-text-primary font-semibold">{data.season_summary.avg_pts} avg</span>
                  {' · '}
                  <span className="text-text-primary font-semibold">{data.season_summary.games_played} GP</span>
                  {data.season_summary.position_rank && (
                    <>
                      {' · '}
                      <span className="text-accent font-semibold">
                        <span className="md:hidden">{data.player.position}{data.season_summary.position_rank}</span>
                        <span className="hidden md:inline">{data.player.position} Rank #{data.season_summary.position_rank}</span>
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Player Notes — current blurb on top, prior weeks collapsed
                behind a toggle so they don't push everything down. Falls
                back to ESPN injury text only when no blurb exists at all. */}
            <PlayerNotesSection blurbs={data.blurbs} blurb={data.blurb} injuryDetail={data.injury_detail} />
            {(data.blurbs?.length || data.blurb || data.injury_detail?.body_part || data.injury_detail?.detail) && (
              <div className="border-t border-text-primary/10" />
            )}

            {/* Current week narrative — only render when there are
                stats for the latest played week. Pre-season / DNP
                players would otherwise show "This Week / No stats yet
                this week" which is noise, not info. */}
            {data.current_week && (
              <>
                <div>
                  <h3 className="text-xs uppercase text-text-muted tracking-wider mb-2">
                    Week {data.current_week.week}
                  </h3>
                  <CurrentWeekNarrative position={data.player.position} week={data.current_week} hideFantasyPoints={hideFantasyPoints} />
                </div>
                <div className="border-t border-text-primary/10" />
              </>
            )}

            {/* Weekly table — played weeks above the line, upcoming
                weeks below with empty stats. Header dropped since the
                table is self-explanatory (past = filled, future =
                muted blanks). */}
            <div>
              <PreviousGamesTable
                hideFantasyPoints={hideFantasyPoints}
                position={data.player.position}
                weeks={data.weekly_stats}
                currentWeek={data.current_week}
              />
            </div>

            {/* News & Updates */}
            {data.news && data.news.length > 0 && (
              <>
                <div className="border-t border-text-primary/10" />
                <div>
                  <h3 className="text-xs uppercase text-text-muted tracking-wider mb-2">News & Updates</h3>
                  <div className="space-y-2">
                    {data.news.map((article, idx) => (
                      <div
                        key={idx}
                        className="rounded-xl border border-text-primary/10 bg-bg-primary p-3"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-bold text-text-primary leading-snug">{article.headline}</h4>
                          {article.type && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-accent/20 text-accent shrink-0">{article.type}</span>
                          )}
                        </div>
                        {article.description && (
                          <p className="text-xs text-text-secondary leading-relaxed">{article.description}</p>
                        )}
                        {article.published && (
                          <div className="text-[10px] text-text-muted mt-1.5">
                            {new Date(article.published).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {/* Contextual action button */}
            {playerContext && (
              <div className="flex justify-center pt-2 pb-1">
                {playerContext === 'my_roster' && onDrop && (
                  // A started STARTER cannot be dropped — his points are
                  // already in this week's score. The server refuses it
                  // (fantasyService: lockedTeams + isStarterSlot), and
                  // without this the only way to find out was to tap Drop,
                  // confirm "Are you sure?", and then read an error. The
                  // reason belongs on the button, before the decision.
                  //
                  // Bench and IR stay droppable even when locked, which is
                  // deliberate and matches Yahoo — a non-scoring slot cannot
                  // change what the lineup already earned.
                  dropBlockedReason ? (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        disabled
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-text-muted font-semibold text-xs opacity-50 cursor-not-allowed"
                      >
                        <span className="text-sm leading-none">−</span>
                        Drop
                      </button>
                      <p className="text-[11px] text-text-muted text-center max-w-[15rem] leading-snug">
                        {dropBlockedReason}
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => onDrop(playerId)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-incorrect hover:bg-incorrect/10 transition-colors font-semibold text-xs"
                    >
                      <span className="text-sm leading-none">−</span>
                      Drop
                    </button>
                  )
                )}
                {playerContext === 'opponent' && onTrade && (
                  <button
                    onClick={() => onTrade(playerId)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent/15 text-accent hover:bg-accent/25 transition-colors font-semibold text-sm"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
                      <polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
                    </svg>
                    Propose Trade
                  </button>
                )}
                {playerContext === 'waiver' && onClaim && (
                  /* Orange BORDER, transparent interior — the house treatment
                     for accent actions. A filled tint at this size reads as a
                     solid block of colour rather than a button. */
                  <button
                    onClick={() => onClaim(playerId)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-accent text-accent hover:bg-accent/10 transition-colors font-semibold text-sm"
                  >
                    <span className="w-6 h-6 rounded-full bg-accent/30 flex items-center justify-center text-lg leading-none">+</span>
                    Claim
                  </button>
                )}
                {playerContext === 'free_agent' && onAdd && (
                  <button
                    onClick={() => onAdd(playerId)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-correct/15 text-correct hover:bg-correct/25 transition-colors font-semibold text-sm"
                  >
                    <span className="w-6 h-6 rounded-full bg-correct/30 flex items-center justify-center text-lg leading-none">+</span>
                    Add
                  </button>
                )}
              </div>
            )}
          </div>
          </div>
          )
        })()}
      </div>
    </div>,
    document.body
    )
  )
}
